import React, { useEffect, useRef, useState, useCallback } from "react";

/* ---------- scroll-reveal hook ---------- */
export function useReveal(threshold = 0.2) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ---------- animated counter ---------- */
export function Counter({ to, prefix = "", suffix = "", decimals = 0, duration = 1400 }) {
  const [ref, visible] = useReveal(0.5);
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start = null;
    let raf;
    const step = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [visible, to, duration]);
  return (
    <span ref={ref} className="counter">
      {prefix}
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
}

/* ---------- reveal wrapper ---------- */
export function Reveal({ as: Tag = "div", delay = 0, className = "", children, style = {}, id }) {
  const [ref, visible] = useReveal(0.15);
  return (
    <Tag
      ref={ref}
      id={id}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  );
}

/* ---------- comic starburst badge ---------- */
export function Burst({ children, className = "" }) {
  return (
    <span className={`burst ${className}`}>
      <span>{children}</span>
    </span>
  );
}

/* ---------- tilt card (light 3D on pointer move) ---------- */
export function TiltCard({ children, className = "" }) {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(700px) rotateX(${py * -8}deg) rotateY(${px * 10}deg) translateZ(6px)`;
  }, []);
  const onLeave = useCallback(() => {
    const el = ref.current;
    if (el) el.style.transform = "perspective(700px) rotateX(0deg) rotateY(0deg)";
  }, []);
  return (
    <div ref={ref} className={`tilt-card ${className}`} onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
    </div>
  );
}

/* ---------- WhatsApp-style conversational admin demo ---------- */
export function WhatsAppDemo() {
  const [ref, visible] = useReveal(0.4);
  const [step, setStep] = useState(0);
  const messages = [
    { from: "user", text: "book the tennis court for 7 PM" },
    { from: "bot", text: "Checking Court 1, 7:00\u20138:00 PM\u2026" },
    { from: "bot", text: "Done! Court 1 is booked, 7\u20138 PM tonight." },
  ];
  useEffect(() => {
    if (!visible) return;
    setStep(0);
    const timers = messages.map((_, i) => setTimeout(() => setStep(i + 1), 500 + i * 950));
    return () => timers.forEach(clearTimeout);
  }, [visible]);

  return (
    <div className="demo-panel" ref={ref}>
      <div className="demo-panel-head">
        <span className="demo-avatar">B</span>
        <span className="demo-head-text">BELLBOY CHAT</span>
      </div>
      <div className="demo-body">
        {messages.slice(0, step).map((m, i) => (
          <div key={i} className={`speech ${m.from === "user" ? "speech-me" : "speech-them"}`}>
            {m.text}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- payments-incoming + RWA visibility demo ---------- */
export function PaymentDemo() {
  const [ref, visible] = useReveal(0.4);
  const [phase, setPhase] = useState(0);
  const ledger = [
    { flat: "A-402", name: "R. Sharma", status: "paid" },
    { flat: "A-403", name: "K. Iyer", status: "paid" },
    { flat: "B-101", name: "S. Verma", status: "pending" },
  ];
  useEffect(() => {
    if (!visible) return;
    setPhase(0);
    const t1 = setTimeout(() => setPhase(1), 500);
    const t2 = setTimeout(() => setPhase(2), 1400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [visible]);

  return (
    <div className="demo-panel" ref={ref}>
      <div className="demo-panel-head">
        <span className="demo-avatar">₹</span>
        <span className="demo-head-text">RWA COLLECTIONS</span>
      </div>
      <div className="demo-body">
        {phase >= 1 && (
          <div className="pay-toast">
            <Burst className="burst-small">CHA-CHING!</Burst>
            <div className="pay-toast-text">
              <strong>₹2,500</strong> maintenance received from <strong>A-402</strong>
            </div>
          </div>
        )}
        {phase >= 2 && (
          <div className="ledger">
            {ledger.map((row) => (
              <div key={row.flat} className="ledger-row">
                <span className="ledger-flat">{row.flat}</span>
                <span className="ledger-name">{row.name}</span>
                <span className={`stamp ${row.status === "paid" ? "stamp-paid" : "stamp-pending"}`}>
                  {row.status === "paid" ? "PAID" : "PENDING"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- SEO: keep document head in sync with the active page ---------- */
function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function upsertLink(rel, href, extra = {}) {
  const selector = `link[rel="${rel}"][href="${href}"]`;
  if (document.head.querySelector(selector)) return;
  const el = document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
}
export function useSEO({ title, description, indexable = true }) {
  useEffect(() => {
    document.documentElement.lang = "en";
    upsertLink("preconnect", "https://fonts.googleapis.com");
    upsertLink("preconnect", "https://fonts.gstatic.com", { crossorigin: "anonymous" });
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("name", "robots", indexable ? "index, follow" : "noindex, nofollow");
  }, [title, description, indexable]);
}
